import { useState } from 'react';
import { Post } from 'lib/types';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { Chip, IconButton } from '@mui/material';
import { A } from 'components/A';
import { buildProjectPath } from 'lib/helpers';
import { SavePostToProjectsButton } from 'components/SaveToList/SavePostToProjectsButton';
import AddCircleRounded from '@mui/icons-material/AddCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { Project } from 'lib/types/projects';
import { useSnackbar } from 'notistack';

interface Props {
    post: Post;
}

export const PostProjects = ({ post }: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  const { data: projects } = useQuery({
    queryKey: ['getProjectsForPost', post.id],
    queryFn: () => API.getProjectsForPost(post.id),
    enabled: !!post.id && !!currentUser,
  });

  const deletePostFromProject = useMutation({
    mutationFn: async (project: Project) => {
      await API.removePostFromProject(project.id, post.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getProjectsForPost', post.id] });
      enqueueSnackbar('Post removed from project successfully', { variant: 'success' });
      setProjectToDelete(null);
    },
    onError: (error: any) => {
      enqueueSnackbar(`Error removing post from project: ${error.message}`, { variant: 'error' });
      setProjectToDelete(null);
    },
  });

  const handleDeleteClick = (project: Project) => {
    setProjectToDelete(project);
  };

  const handleConfirmDelete = () => {
    if (projectToDelete) {
      deletePostFromProject.mutate(projectToDelete);
    }
  };

  const handleCancelDelete = () => {
    setProjectToDelete(null);
  };

  const deleteIcon = <CloseRounded className="text-white" />;

  return (
    <>
      <div className="flex flex-wrap md:flex-nowrap items-center">
        <div className="w-full md:w-4/5 py-2">
          <ul className="flex flex-wrap gap-2 list-none">
            {projects?.map((p) => (
              <Chip
                key={p.id}
                size="small"
                label={(
                  <A
                    href={buildProjectPath(p)}
                    className="text-white font-bold"
                  >
                    {p.name}
                  </A>
                )}
                // @ts-ignore
                color="alpineTeal"
                deleteIcon={deleteIcon}
                onDelete={() => handleDeleteClick(p)}
              />
            ))}
            <IconButton
              aria-label="Add project"
              // @ts-ignore
              color="alpineTeal"
              sx={{ padding: 0 }}
            >
              <AddCircleRounded />
              <SavePostToProjectsButton styling="ml-1 text-sm font-semibold flex align-center" postId={post.id} text="Add to project" />
            </IconButton>
          </ul>
        </div>
      </div>
      {projectToDelete && (
        <ConfirmationModal
          title="Remove post from project"
          text={`Are you sure you want to remove this post from "${projectToDelete.name}"?`}
          onYes={handleConfirmDelete}
          onNo={handleCancelDelete}
        />
      )}
    </>
  );
};
